import {
  browserSupportsWebAuthn,
  getBrowserCapabilities,
  platformAuthenticatorIsAvailable,
} from "@simplewebauthn/browser";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const PASSKEY_SUPPORT_QUERY_KEY = ["passkey-support"] as const;

/**
 * Only a definite "no" counts: a browser without WebAuthn, or one that reports no passkey store,
 * no sign-in with a phone and no platform authenticator. Anything it does not divulge is worth a
 * try. The last check is there because some authenticators (Playwright's virtual one) only
 * answer `isUserVerifyingPlatformAuthenticatorAvailable()`.
 */
async function detectPasskeySupport(): Promise<boolean> {
  if (!browserSupportsWebAuthn()) return false;
  try {
    const capabilities = await getBrowserCapabilities();
    if (
      capabilities.passkeyPlatformAuthenticator !== "unsupported" ||
      capabilities.hybridTransport !== "unsupported"
    ) {
      return true;
    }
    return await platformAuthenticatorIsAvailable();
  } catch {
    return true;
  }
}

/**
 * `true` until proven otherwise, so the server render and most devices never flip. A ceremony
 * that fails with NotSupportedError (ChromeOS without a WebAuthn service) passes all checks up
 * front, so the flows report it through `markUnsupported`.
 */
export function usePasskeySupport() {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: PASSKEY_SUPPORT_QUERY_KEY,
    queryFn: detectPasskeySupport,
    staleTime: Infinity,
  });
  return {
    isSupported: data ?? true,
    markUnsupported: () => queryClient.setQueryData(PASSKEY_SUPPORT_QUERY_KEY, false),
  };
}
